import type { PolymorphicRef } from '@sds-system/types';
import type { ChangeEvent, ElementType } from 'react';
import { forwardRef, useEffect, useState } from 'react';
import { StyledToggleSwitchHandler, StyledToggleSwitchRoot } from './ToggleSwitch.styled';
import type { ToggleSwitchComponent, ToggleSwitchProps } from './ToggleSwitch.types';

export const ToggleSwitch: ToggleSwitchComponent = forwardRef(
  <C extends ElementType = 'input'>(props: ToggleSwitchProps<C>, ref: PolymorphicRef<C>) => {
    const {
      checked = false,
      disabled = false,
      styledComponents,
      size = 'M',
      ...inputProps
    } = props;
    const innerName = inputProps.name || 'ToggleSwitch';

    const [innerState, setInnerState] = useState(checked || false);

    const handleChangeChecked = (e: ChangeEvent<HTMLInputElement>) => {
      inputProps.onChange?.(e);
      disabled || setInnerState((p) => !p);
    };

    useEffect(() => {
      setInnerState(checked || false);
    }, [checked]);

    return (
      <StyledToggleSwitchRoot
        checked={innerState}
        disabled={disabled}
        htmlFor={innerName}
        {...(styledComponents?.root && styledComponents.root)}
        size={size}
      >
        <input
          {...inputProps}
          checked={innerState || false}
          disabled={disabled}
          hidden
          id={innerName}
          onChange={handleChangeChecked}
          ref={ref}
          type='checkbox'
        />

        <StyledToggleSwitchHandler
          checked={innerState}
          disabled={disabled}
          {...(styledComponents?.handler && styledComponents.handler)}
        />
      </StyledToggleSwitchRoot>
    );
  },
);
